'use client';

import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Consent } from '@personly/libs-widgets';
import type { CookieType } from '@personly/libs-types';
import { useCookies } from '@personly/libs-providers';

const toTypeHandles = (cookieTypes: CookieType[]) =>
  cookieTypes.map(({ handle }) => handle);

type Context = {
  consentSettingsOpen: boolean;
  setConsentSettingsOpen: Dispatch<SetStateAction<boolean>>;
  selectedCookieType: CookieType;
  setSelectedCookieType: Dispatch<SetStateAction<CookieType>>;
  selectedCookieTypes: string[];
  setSelectedCookieTypes: Dispatch<SetStateAction<string[]>>;
};

const ConsentsContext = createContext<Context | null>(null);

interface Props {
  children: ReactNode;
  cookieTypes: CookieType[];
  consentLabel: string;
}

const ConsentsProvider = ({ children, cookieTypes, consentLabel }: Props) => {
  const { consent } = useCookies();

  const [openAfterLightHouse, setOpenAfterLighthouse] = useState(false);
  const [consentSettingsOpen, setConsentSettingsOpen] = useState(false);
  const [selectedCookieType, setSelectedCookieType] = useState(cookieTypes[0]);
  const [selectedCookieTypes, setSelectedCookieTypes] = useState(
    toTypeHandles(cookieTypes)
  );

  useEffect(() => {
    setTimeout(() => setOpenAfterLighthouse(true), 3000);
  }, []);

  const isOpen = useMemo(
    () => consent.createdAt === 0 && openAfterLightHouse,
    [consent, openAfterLightHouse]
  );

  const value = useMemo(
    () => ({
      consentSettingsOpen,
      setConsentSettingsOpen,
      selectedCookieType,
      setSelectedCookieType,
      selectedCookieTypes,
      setSelectedCookieTypes,
    }),
    [
      consentSettingsOpen,
      setConsentSettingsOpen,
      selectedCookieType,
      setSelectedCookieType,
      selectedCookieTypes,
      setSelectedCookieTypes,
    ]
  );

  return (
    <ConsentsContext.Provider value={value}>
      <Consent isOpen={isOpen} consentLabel={consentLabel}>
        {children}
      </Consent>
    </ConsentsContext.Provider>
  );
};

function useConsents() {
  const context = useContext(ConsentsContext);
  if (!context) throw new Error('useConsents requires a context!');

  return context;
}

export { ConsentsProvider, useConsents };
